'use client';

import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Mic, Loader2 } from "lucide-react";
import { MicVAD } from '@ricky0123/vad-web';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from 'next-intl';

interface SpeechToTextButtonProps {
  onTranscriptionComplete: (text: string) => void;
  disabled?: boolean;
  language?: string;
  prevContent?: string;
}

const SILENCE_TIMEOUT_MS = 3000; // 3 seconds of silence before stopping

export function TranscribeButton(props: SpeechToTextButtonProps) {
  const [recording, setRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const isRecordingRef = useRef(false); // Add this ref to track recording state
  const audioChunks = useRef<Blob[]>([]);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const vadInstance = useRef<any>(null);
  const mediaStream = useRef<MediaStream | null>(null);
  const recordingTimeout = useRef<NodeJS.Timeout | null>(null); // Add this line
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const t = useTranslations('transcribe');

  const buildTranscriptionPrompt = () => {
    let prompt = '';

    if (props.language?.trim()) {
      prompt = `Language is ${props.language}! Previous transcription: `;
    }

    if (props.prevContent?.trim()) {
      prompt += props.prevContent.slice(-200);
    }

    return prompt;
  };

  // Update the ref whenever recording state changes
  useEffect(() => {
    isRecordingRef.current = recording;
  }, [recording]);

  // Clean up function to be called when recording ends
  const cleanupResources = () => {
    try {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }

      if (recordingTimeout.current) {
        clearTimeout(recordingTimeout.current);
        recordingTimeout.current = null;
      }

      // Stop VAD first
      if (vadInstance.current) {
        try {
          vadInstance.current.destroy();
        } catch (err) {
          console.error('Error destroying VAD:', err);
        }
        vadInstance.current = null;
      }

      // Then stop MediaRecorder
      if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
        try {
          mediaRecorder.current.stop();
        } catch (err) {
          console.error('Error stopping MediaRecorder:', err);
        }
        mediaRecorder.current = null;
      }

      // Finally stop the media stream tracks
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach(track => track.stop());
        mediaStream.current = null;
      }
    }
    catch (err) {
      console.error('Error cleaning up resources: ', err);
    }
  };

  const getSupportedType = () => {
    const mimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4'
    ];

    return mimeTypes.find(type => MediaRecorder.isTypeSupported(type));
  }


  const stopRecording = async () => {
    // Check the ref instead of the state
    if (!isRecordingRef.current) {
      return;
    }

    setRecording(false);
    setIsProcessing(true);

    // Clean up recording resources
    cleanupResources();

    // Small delay to ensure the final data is collected
    await new Promise(resolve => setTimeout(resolve, 100));

    if (audioChunks.current.length > 0) {
      const audioBlob = new Blob(audioChunks.current, { type: getSupportedType() });
      await transcribeAudio(audioBlob);
    } else {
      console.warn("No audio data was captured");
      setIsProcessing(false);
    }
  };

  const handleToggleRecording = async () => {
    if (recording) {
      await stopRecording();
      return;
    }

    try {
      setIsProcessing(true);
      audioChunks.current = [];

      // Set up 10-minute timeout
      recordingTimeout.current = setTimeout(() => {
        console.log("Recording timeout reached (10 minutes)");
        stopRecording();
      }, 10 * 60 * 1000);

      // First get the audio stream directly
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Force mono
          sampleRate: 16000
        }
      });
      mediaStream.current = stream;

      const supportedType = getSupportedType();

      console.info('Supported audio type:', supportedType);

      if (!supportedType) {
        throw new Error('No supported audio type found');
      }

      // Create and start MediaRecorder first
      const recorder = new MediaRecorder(stream, {
        mimeType: supportedType
      });

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunks.current.push(e.data);
        }
      };

      recorder.start(1000); // Get data every second
      mediaRecorder.current = recorder;

      // Initialize VAD - this will get its own stream internally
      const vad = await MicVAD.new({
        onSpeechEnd: () => {
          console.log("Speech ended, stopping recording");

          // Clear any existing timeout
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }

          // Set new timeout
          silenceTimeoutRef.current = setTimeout(() => {
            console.log("Silence threshold reached, stopping recording");
            stopRecording();
          }, SILENCE_TIMEOUT_MS);
        },
        onSpeechStart: () => {
          console.log("Speech detected, starting recording");

          // Clear the timeout if speech starts again
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        }
        // With frameSamples at 1024 and a sample rate of 16kHz, each frame is approximately 64ms. 
        // So if you want 2 seconds of silence before stopping, you would set redemptionFrames to 
        // around... 31 (2000ms / 64ms).
        // But, we won't do that now. Just a future reference guide because it's fucking complicated
        // apparently.
      });

      vadInstance.current = vad;

      // Start the VAD
      vad.start();

      setIsProcessing(false);
      setRecording(true);

    } catch (err) {
      console.error('Error starting recording:', err);
      cleanupResources();
      setIsProcessing(false);
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    try {
      console.log(`Audio blob size: ${audioBlob.size} bytes`);

      let processedBlob = audioBlob;

      if (isIOS()) {
        console.log('Converting mp4 to wav for iOS');
        processedBlob = await convertAudioToMono(audioBlob);
      }

      console.log(`Processed audio blob size: ${processedBlob.size} bytes`);

      const formData = new FormData();
      formData.append('file', processedBlob);
      formData.append('prompt', buildTranscriptionPrompt());

      const response = await fetch('/api/transcribe', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const { transcription } = await response.json();

      props.onTranscriptionComplete(transcription?.trim());
    } catch (err) {
      console.error('Transcription error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupResources();
    };
  }, []);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={handleToggleRecording}
            disabled={props.disabled || isProcessing}
            className={recording ? "bg-red-500 hover:bg-red-600" : ""}
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mic className={recording ? "animate-pulse" : ""} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('buttonTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function isIOS() {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
}

async function convertAudioToMono(file: File | Blob): Promise<Blob> {
  const audioContext = new AudioContext({ sampleRate: 16000 });
  const arrayBuffer = await file.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  // Create offline context for processing
  const offlineContext = new OfflineAudioContext(1, audioBuffer.length, 16000);
  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineContext.destination);
  source.start();

  // Render audio
  const renderedBuffer = await offlineContext.startRendering();

  // Convert to WAV format
  const length = renderedBuffer.length * 2;
  const buffer = new ArrayBuffer(44 + length);
  const view = new DataView(buffer);

  // WAV header
  const writeString = (view: DataView, offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, 16000, true);
  view.setUint32(28, 32000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, length, true);

  // Write audio data
  const data = new Float32Array(renderedBuffer.getChannelData(0));
  let offset = 44;
  for (let i = 0; i < data.length; i++) {
    const sample = Math.max(-1, Math.min(1, data[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: "audio/wav" });
}